// Components
import { Tooltip, UnstyledButton } from "@mantine/core";
import { IconPresentation } from "@tabler/icons-react";

import classes from "./ConnectionsAppShell.module.css";

/**
 * Props for {@link DemoModeToggle}.
 * @agentic connections
 */
export type DemoModeToggleProps = {
  /** Whether demo mode is currently on. */
  enabled: boolean;
  /** Flips demo mode. */
  onToggle: () => void;
  /** Renders the icon-only variant used by the collapsed sidebar. */
  collapsed?: boolean;
};

/**
 * Sidebar-footer switch that hides the internal-only nav entries while
 * presenting to a clinician audience. Purely presentational — availability and
 * persistence live in `useDemoMode`.
 *
 * Unlike the version label beside it, this renders in the collapsed sidebar
 * too: an expanded-only toggle could be stranded on (enable it, collapse the
 * sidebar, and there is no way back).
 * @agentic connections
 */
export const DemoModeToggle = ({
  enabled,
  onToggle,
  collapsed = false,
}: DemoModeToggleProps) => {
  const label = enabled
    ? "Demo mode on — internal tools hidden"
    : "Demo mode off — internal tools visible";

  return (
    <Tooltip label={label} withArrow position="right">
      <UnstyledButton
        className={classes.demoModeToggle}
        data-collapsed={collapsed || undefined}
        data-active={enabled || undefined}
        onClick={onToggle}
        role="switch"
        aria-checked={enabled}
        aria-label="Demo mode"
        data-testid="demo-mode-toggle"
      >
        <IconPresentation size={16} />
      </UnstyledButton>
    </Tooltip>
  );
};
